import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getGradeLevelLabel } from '../../../shared/constant/gradeLevels';
import { useAttendanceTrend } from '../hooks/useAttendanceTrend';
import { MONTH_RANGE, WEEK_RANGE } from '../services/attendanceTrendService';

// Fixed-order categorical palette (validated colorblind-safe, see the dataviz skill) — assigned
// by position, never cycled, and capped at 6 lines so a chart never has to reuse a color.
const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];
const MAX_LINES = PALETTE.length;

const W = 640;
const H = 220;
const PAD = { top: 12, right: 16, bottom: 28, left: 34 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const GRID_STEPS = [0, 25, 50, 75, 100];

// At most this many date labels along the x-axis before thinning them out — five weekdays (week
// view) always show in full; a month's ~20+ weekdays would otherwise overlap.
const MAX_LABELS = 8;

const RANGES = [
  { value: WEEK_RANGE, label: 'Week', title: 'This Week' },
  { value: MONTH_RANGE, label: 'Month', title: 'This Month' },
];

const shortDate = (iso) => {
  const [, m, d] = iso.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${d}`;
};

const sectionLabel = (s) => `${getGradeLevelLabel(s.grade_level)} - ${s.section}`;

// Daily attendance rate per section the teacher handles, toggled between the current week
// (Mon-Fri) and the current calendar month.
function AttendanceTrendChart() {
  const svgRef = useRef(null);
  const [range, setRange] = useState(WEEK_RANGE);
  const [hoverIndex, setHoverIndex] = useState(null);
  const { trend, loading } = useAttendanceTrend(true, range);

  const days = trend?.days ?? [];
  const sections = (trend?.sections ?? []).slice(0, MAX_LINES);
  const hiddenCount = (trend?.sections?.length ?? 0) - sections.length;
  const hasAnyData = sections.some((s) => trend.series[s.key].some((v) => v != null));
  const labelStride = Math.max(1, Math.ceil(days.length / MAX_LABELS));

  const xAt = (i) => (days.length <= 1 ? PAD.left + PLOT_W / 2 : PAD.left + (i / (days.length - 1)) * PLOT_W);
  const yAt = (v) => PAD.top + PLOT_H - (v / 100) * PLOT_H;

  const linePath = (values) => {
    let d = '';
    let drawing = false;
    values.forEach((v, i) => {
      if (v == null) {
        drawing = false;
        return;
      }
      d += `${drawing ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)} `;
      drawing = true;
    });
    return d.trim();
  };

  const handleMove = (e) => {
    if (days.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const ratio = Math.min(1, Math.max(0, (x - PAD.left) / PLOT_W));
    setHoverIndex(Math.round(ratio * (days.length - 1)));
  };

  const handleRangeChange = (value) => {
    setRange(value);
    setHoverIndex(null);
  };

  const rangeTitle = RANGES.find((r) => r.value === range).title;

  return (
    <section className="bg-white rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-[#0b4a71]">Attendance Trend by Section ({rangeTitle})</h2>

        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-[#4ea6d0] overflow-hidden text-xs">
            {RANGES.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => handleRangeChange(r.value)}
                aria-pressed={range === r.value}
                className={`px-3 py-1.5 font-medium transition ${
                  range === r.value ? 'bg-[#0b4a71] text-white' : 'text-[#0b4a71] hover:bg-[#4ea6d0]/10'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <Link to="/dashboard/attendance-summary" className="text-xs font-medium text-[#3f6d8e] hover:underline shrink-0">
            View details
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="h-[220px] rounded bg-gray-100 animate-pulse" />
      ) : sections.length === 0 || !hasAnyData ? (
        <p className="text-sm text-gray-500 py-8 text-center">
          {sections.length === 0
            ? 'No sections are assigned to you yet.'
            : `No attendance recorded ${range === WEEK_RANGE ? 'so far this week' : 'yet this month'}.`}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {sections.map((s, i) => (
              <span key={s.key} className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: PALETTE[i] }} aria-hidden="true" />
                {sectionLabel(s)}
              </span>
            ))}
            {hiddenCount > 0 && <span className="text-xs text-gray-400">+{hiddenCount} more not shown</span>}
          </div>

          <div className="relative">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              className="w-full h-[220px]"
              onMouseMove={handleMove}
              onMouseLeave={() => setHoverIndex(null)}
              role="img"
              aria-label={`Line chart of daily attendance rate per section for ${rangeTitle.toLowerCase()}`}
            >
              {GRID_STEPS.map((v) => (
                <g key={v}>
                  <line x1={PAD.left} x2={W - PAD.right} y1={yAt(v)} y2={yAt(v)} stroke="#f3f4f6" strokeWidth="1" />
                  <text x={PAD.left - 6} y={yAt(v)} textAnchor="end" dominantBaseline="middle" fontSize="9" fill="#9ca3af">
                    {v}
                  </text>
                </g>
              ))}

              {days.map(
                (day, i) =>
                  (i % labelStride === 0 || i === days.length - 1 || i === hoverIndex) && (
                    <text
                      key={day}
                      x={xAt(i)}
                      y={H - 8}
                      textAnchor="middle"
                      fontSize="9"
                      fill={i === hoverIndex ? '#0b4a71' : '#9ca3af'}
                      fontWeight={i === hoverIndex ? '600' : '400'}
                    >
                      {shortDate(day)}
                    </text>
                  )
              )}

              {hoverIndex != null && (
                <line
                  x1={xAt(hoverIndex)}
                  x2={xAt(hoverIndex)}
                  y1={PAD.top}
                  y2={PAD.top + PLOT_H}
                  stroke="#c3c2b7"
                  strokeWidth="1"
                />
              )}

              {sections.map((s, i) => (
                <g key={s.key}>
                  <path d={linePath(trend.series[s.key])} fill="none" stroke={PALETTE[i]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  {trend.series[s.key].map(
                    (v, di) =>
                      v != null && (
                        <circle
                          key={di}
                          cx={xAt(di)}
                          cy={yAt(v)}
                          r={di === hoverIndex ? 4.5 : 3}
                          fill={PALETTE[i]}
                          stroke="#ffffff"
                          strokeWidth="1.5"
                        />
                      )
                  )}
                </g>
              ))}
            </svg>

            {hoverIndex != null && (
              <div
                className="absolute top-1 pointer-events-none bg-[#0b4a71] text-white text-xs rounded-md px-3 py-2 shadow-lg space-y-0.5 z-10"
                style={{
                  left: `${(xAt(hoverIndex) / W) * 100}%`,
                  transform: `translateX(${hoverIndex > days.length / 2 ? '-100%' : '0%'})`,
                }}
              >
                <p className="font-semibold">{shortDate(days[hoverIndex])}</p>
                {sections.map((s, i) => {
                  const v = trend.series[s.key][hoverIndex];
                  return (
                    <p key={s.key} className="flex items-center gap-1.5 whitespace-nowrap">
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: PALETTE[i] }} aria-hidden="true" />
                      {sectionLabel(s)}: {v == null ? '—' : `${Math.round(v)}%`}
                    </p>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export default AttendanceTrendChart;
